















import { parseLocaleNumber } from "@/components/numbers/numberFormatUtils";
import { Currency } from "@/shared/models/currency";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";

export type PriceChange = {
  value: number | null;
  percentage: boolean;
  addition: boolean;
  subtraction: boolean;
  valid: boolean;
};

@Component
export default class PriceInput extends Vue {
  @Prop() value?: string;
  @Prop() currencyId?: number;
  @Prop() label?: string;
  @Prop() disableRules?: boolean;
  @Prop() dense?: boolean;
  @Prop({ default: true }) required?: boolean;
  @Prop({ default: true }) changesOriginalPrice?: boolean;
  @Prop() placeholderHint?: string;
  @Prop({ default: true }) outlined?: boolean;
  @Prop({ default: false }) forPromoCode?: boolean;
  @Prop({ default: false }) forRackRates?: boolean;

  isInputFocused = false;

  @Watch("changesOriginalPrice")
  changesOriginalPriceWatch() {
    this.formatHint();
  }

  priceRule(v: string) {
    return ((v || this.required) && /^(\+|-)?([\d,.]+)(%)?$/.test(v)) ||
      (!this.required && !v)
      ? true
      : this.$t("Calendar.PriceEditor.Invalid");
  }

  maxDecimals(value: any) {
    if (value !== undefined) {
      return !(this.countDecimals(value) > 2)
        ? true
        : this.$t("Validation.MaxDecimals{max}", {
            max: 2,
          });
    } else {
      return true;
    }
  }

  get decimalSeparator() {
    const separator = Intl.NumberFormat(this.internalLanguage)
      .format(1.1)
      .replace(/\p{Number}/gu, "");
    return separator;
  }

  countDecimals(num: number) {
    if (num != null) {
      const decimalPart = num
        .toString()
        .split(this.decimalSeparator)[1]
        ?.replaceAll(/[^\d]/g, "");
      return decimalPart?.length || 0;
    }
    return 0;
  }

  // prettier-ignore
  @Action("codebooks/refreshCurrencies") refreshCurrencies?: () => Promise<void>;
  @Getter("codebooks/currencies") currencies?: Currency[];
  @Getter("language/language") language?: string;

  get internalLanguage() {
    return this.language ?? "hr-HR";
  }

  async created() {
    if ((this.currencies?.length ?? 0) === 0 && this.refreshCurrencies) {
      this.refreshCurrencies();
    }
    if(this.forPromoCode && this.value && !this.lastPriceChange){
      this.onInput(this.value);
    }
  }

  hint = "";
  lastPriceChange?: PriceChange;
  rackRatePriceChange?: PriceChange;

  onInput(v: string) {
    this.$emit("input", v);

    const groups = /^(\+|-)?([\d,.]+)(%)?$/.exec(v ?? "");

    if (groups) {
      const flat = [...groups];
      const percentage = flat[3] === "%";
      const subtraction = flat[1] === "-";
      const addition = flat[1] === "+" || (percentage && !flat[1]);
      const number = parseLocaleNumber(flat[2], this.internalLanguage);

      const valueCurrency = this.valueAppendCurrencyForDisplay(number);

      const res = {
        value: number,
        valueCurrency: valueCurrency,
        percentage,
        addition,
        subtraction,
        valid: true,
      } as PriceChange;

      this.lastPriceChange = res;
      this.rackRatePriceChange = res;

      this.formatHint();

      this.$emit("change", res);
    } else {
      this.hint = "";

      const value = this.required ? 0 : null;
      const toEmit = {
        value,
        valueCurrency: this.valueAppendCurrencyForDisplay(value ?? 0),
        percentage: false,
        addition: false,
        subtraction: false,
        valid: !this.required && v === "" ? true : false,
      };
      this.rackRatePriceChange = toEmit;
      this.$emit("change", toEmit);
    }
  }

  valueAppendCurrencyForDisplay(number: number) {
    let valueCurrency = number.toString();
    if (this.currencyId) {
      try {
        const currency = this.currencies?.find((x) => x.id === this.currencyId);
        if (currency) {
          valueCurrency = this.$n(number, {
            style: "currency",
            currency: currency.isoCode,
            currencyDisplay: "code",
          });
        }
      } catch {
        /* ignore */
      }
    }

    return valueCurrency;
  }

  formatHint() {
    if (!this.lastPriceChange) return;

    if (this.forRackRates) {
      this.hint = "";
      return;
    }

    let tr = "";
    let addOn = "";

    if (!this.forPromoCode) {
      if (this.lastPriceChange.percentage) {
        if (this.lastPriceChange.subtraction) {
          tr = "Calendar.PriceEditor.Percentage.Sub";
          addOn = this.changesOriginalPrice
            ? "Calendar.PriceEditor.FromOriginalPrice"
            : "Calendar.PriceEditor.FromNewPrice";
        } else {
          tr = "Calendar.PriceEditor.Percentage.Add";
          addOn = this.changesOriginalPrice
            ? "Calendar.PriceEditor.ToOriginalPrice"
            : "Calendar.PriceEditor.ToNewPrice";
        }
      } else {
        if (this.lastPriceChange.addition) {
          tr = "Calendar.PriceEditor.Absolute.Add";
          addOn = this.changesOriginalPrice
            ? "Calendar.PriceEditor.ToOriginalPrice"
            : "Calendar.PriceEditor.ToNewPrice";
        } else if (this.lastPriceChange.subtraction) {
          tr = "Calendar.PriceEditor.Absolute.Sub";
          addOn = this.changesOriginalPrice
            ? "Calendar.PriceEditor.FromOriginalPrice"
            : "Calendar.PriceEditor.FromNewPrice";
        } else {
          tr = "Calendar.PriceEditor.Absolute.Set";
        }
      }
    } else {
      if (this.lastPriceChange.percentage) {
        tr = this.lastPriceChange.value != null && this.lastPriceChange.value > 0 && !this.lastPriceChange.subtraction ? 
            "RatePlan.PromoCode.Percentage.Sub.Positive" : 
            "RatePlan.PromoCode.Percentage.Sub";
      } else {
        tr = this.lastPriceChange.value != null && this.lastPriceChange.value > 0 && !this.lastPriceChange.subtraction ? 
            "RatePlan.PromoCode.Absolute.Sub.Positive" : 
            "RatePlan.PromoCode.Absolute.Sub";
      }
    }
    
    this.hint = this.$t(tr, this.lastPriceChange).toString();
    
    this.hint += !this.forPromoCode ? " " + this.$t(addOn).toString() : "";
  }
  calculatePrice() {
    this.isInputFocused = false;
    this.formatHint();
    this.$emit("calculatePrice", this.rackRatePriceChange);
  }
}
